import { Emitter } from "../../../../../emitter";
import { http } from "../../../http";
import { debouncePromised } from "../debouncePromised";
import { getIsVerifiedUrl, getSiteKey, getVerifyUrl } from "./config";
import { handleSessionExpired } from '../../../handleSessionExpired';

/**
 * Служебный класс
 * 
 * Капча для всей страницы или определенной формы
 */
export class Captcha extends Emitter {
    constructor(options = { hideShield: true }) {
        super();
        this.siteKey = getSiteKey();
        this.verifyUrl = getVerifyUrl();
        this.isVerifiedUrl = getIsVerifiedUrl();
        this.pending = false;
        this.hideShield = options.hideShield;
        this._isVerified = false;
        this.challengeVisible = false;

        this.enabled = true; // temporarily always enabled. maby later can use window.config.ysc_enabled === '1'

        /**
         * Промис выполняется после первого полученного токена капчи яндекса
         */
        this.waitAnyToken = debouncePromised(() => new Promise((res, rej) => {
            const timeoutId = setTimeout(() => {
                if (!this.challengeVisible) rej('wait token yandex capthca timeout');
            }, 5000);

            this.once('recieved-token',
                (token) => {
                    clearTimeout(timeoutId);

                    if (!token) return rej('smartcapthca: has no returned token');

                    res();
                },
            );
        }))

        /**
         * Обновляет токен яндекс капчи
         */
        this.execute = debouncePromised(async () => {
            if (!this.enabled) return;

            await this.render();
            
            this.smartCaptcha.reset(this.widgetId);
            this.smartCaptcha.execute(this.widgetId);
            
            await this.waitAnyToken();
        })

        /**
         * Привязать токен к сессии
         * @param {string} reportBugMessage - отправить текст ошибки вместо токена
         */
        this.verify = debouncePromised(async (reportBugMessage) => {
            if (!this.enabled) return;

            const formData = {
                data: {
                    type: "smartCaptchaVerify",
                    attributes: {
                        token: reportBugMessage ?? this.token,
                    }
                }
            }

            const response = await http.post(this.verifyUrl, formData, {
                headers: {'Content-Type': 'application/vnd.api+json'},
                includeSessionId: true,
            })
                .catch(handleSessionExpired);
            if (!response.data.data.attributes.verified) {
                throw new Error('captcha verify error');
            }

            this.isVerified = true;
        })

        /**
         * проверить текущую верификацию
         */
        this.fetchIsVerified = debouncePromised(async () => {
            if (!this.enabled) {
                this.isVerified = true;
                return;
            }

            this.isVerified = true; // reset flag while waiting response

            const response = await http.get(this.isVerifiedUrl, { includeSessionId: true })
                .catch(handleSessionExpired)
                .catch(() => null);
            if (!response?.data?.data?.attributes?.verified) {
                this.isVerified = false;
                return;
            }

            this.isVerified = true;
            return;
        })
    }

    set isVerified(val) {
        this.emit('is-verified-changed', val);
        this._isVerified = val;
    }

    get isVerified() {
        return this._isVerified;
    }

    /**
     * Ждет загрузки скрипта капчи, таймаут 10сек, бросает исключение
     * @returns 
     */
    async _init() {
        if (!this.enabled) return;
        if (this.smartCaptcha) return;

        if (!window.smartCaptcha) {
            await new Promise((res, rej) => {
                const timeoutId = setTimeout(() => rej('load yandex capthca timeout'), 10000);

                window.addEventListener('smartcaptcha-loaded', () => {
                    clearTimeout(timeoutId);
                    res();
                });
            });
        }

        this.smartCaptcha = window.smartCaptcha;
    }

    _createElement() {
        const id = `captcha-${Math.random().toString().slice(2, 10)}`;
        this.el = document.createElement('div');
        this.el.id = id;
        document.body.appendChild(this.el);
    }

    /**
     * Рендер капчи
     * @param {boolean} hideShield - скрыть плашку
     */
    async render(hideShield = this.hideShield) {
        if (!this.enabled) return;

        this.hideShield = hideShield;

        if (this._renderPromise) { // срабатывает только один раз (если нет ошибки), далее возвращает один и тот же промис
            let error;
            await this._renderPromise.catch((e) => {
                error = e;
            });
            
            
            if (error) {
                this._renderPromise = undefined;
            } else {
                return this._renderPromise; // если нет ошибки возвращаем старый промис
            }
        }

        await this._init();

        this._renderPromise = new Promise((res) => {
            this._createElement();
            this.widgetId = this.smartCaptcha.render(this.el, {
                sitekey: this.siteKey,
                hl: 'ru',
                // test: true,
                invisible: true,
                hideShield,
                callback: (token) => {
                    this.emit('recieved-token', token);
                    this.token = token;
                }
            });

            // TODO сейчас нет возможности проверить статус рендера капчи ('network-error' или 'javascript-error' не работают для рендера). когда появится добавить обработчик ошибки и **вернуть исключение**

            this.smartCaptcha.subscribe(this.widgetId, 'challenge-visible', () => this.challengeVisible = true);
            this.smartCaptcha.subscribe(this.widgetId, 'challenge-hidden', () => this.challengeVisible = false);

            res();
        });

        return this._renderPromise;
    }

    /**
     * Проверить капчу/запустить проверку
     * @param {boolean} force - принудительная проверка, когда уже знаем что капча не верифицирована на бэке (например, если какой то из запросов вернул 403)
     */
    async check(force = false) {
        if (!this.enabled) return;

        if (!force) { // если не принудительно, то смотрим на флаг или проверяем на бэке
            if (this.isVerified) return;

            await this.fetchIsVerified();
    
            if (this.isVerified) return;
        }

        let error;
        await this.execute().catch((e) => error = e);
        await this.verify(error);
    }
}

/**
 * Капча для страницы или определенной формы
 * 
 * один инстанс для всего приложения
 */
export const captcha = new Captcha();
